/** @module @liquidefy/code-editor/global/global-permissions @version 2.0.0 @license LQD Research LLC */

// ═══════════════════════════════════════════════════════════════════════════════
// §1 IMPORTS
// ═══════════════════════════════════════════════════════════════════════════════

import type {
  PermissionGrant,
  PermissionRequest,
  PermissionId,
  TrustLevel,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asPermissionId,
  now,
} from '../code-editor.js';

// ═══════════════════════════════════════════════════════════════════════════════
// §2 LOCAL TYPES — PermissionsState, Capability, PendingRequest, PermissionsSummary
// ═══════════════════════════════════════════════════════════════════════════════

/** A capability string describing what a permission allows. */
export type Capability =
  | 'fs.read'
  | 'fs.write'
  | 'network'
  | 'clipboard.read'
  | 'clipboard.write'
  | 'process.spawn'
  | 'storage.local'
  | 'storage.cloud'
  | 'terminal'
  | 'git.read'
  | 'git.write'
  | 'debug'
  | 'ai.query'
  | 'extension.install'
  | 'settings.write'
  | 'ui.notifications'
  | 'ui.webview';

/** A pending permission request awaiting user approval. */
export interface PendingRequest {
  readonly id: string;
  readonly request: PermissionRequest;
  readonly requestedAt: number;
}

/** Full permissions state. */
export interface PermissionsState {
  readonly trustLevel: TrustLevel;
  readonly grants: PermissionGrant[];
  readonly pendingRequests: PendingRequest[];
  readonly revokedIds: Set<PermissionId>;
}

/** Summary of grants by trust level and capability. */
export interface PermissionsSummary {
  readonly trustLevel: TrustLevel;
  readonly totalGrants: number;
  readonly activeGrants: number;
  readonly expiredGrants: number;
  readonly pendingRequests: number;
  readonly byCapability: Map<string, number>;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §3 TRUST LEVEL DEFAULTS — capability sets by trust level
// ═══════════════════════════════════════════════════════════════════════════════

/** Capabilities automatically granted at each trust level. */
export const TRUST_LEVEL_DEFAULTS: Record<TrustLevel, Capability[]> = {
  trusted: [
    'fs.read', 'fs.write', 'network', 'clipboard.read', 'clipboard.write',
    'process.spawn', 'storage.local', 'storage.cloud', 'terminal',
    'git.read', 'git.write', 'debug', 'ai.query', 'extension.install',
    'settings.write', 'ui.notifications', 'ui.webview',
  ],
  workspace: [
    'fs.read', 'fs.write', 'storage.local', 'git.read', 'git.write',
    'terminal', 'ui.notifications', 'settings.write',
  ],
  untrusted: [
    'storage.local', 'ui.notifications',
  ],
};

// ═══════════════════════════════════════════════════════════════════════════════
// §4 FACTORY — createPermissionsState
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Creates a new PermissionsState at the specified trust level.
 * Automatically grants capabilities appropriate for that trust level.
 * @param trustLevel - Initial trust level. Defaults to 'workspace'.
 * @returns Initialized PermissionsState.
 */
export function createPermissionsState(
  trustLevel: TrustLevel = 'workspace'
): PermissionsState {
  const defaults = TRUST_LEVEL_DEFAULTS[trustLevel];
  const grants: PermissionGrant[] = defaults.map((cap) => ({
    id: asPermissionId(generateId('perm-')),
    scope: '*',
    capability: cap,
    trustLevel,
    grantedAt: now(),
    expiresAt: undefined,
  }));

  return {
    trustLevel,
    grants,
    pendingRequests: [],
    revokedIds: new Set(),
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §5 REQUEST & GRANT — requestPermission, grantPermission
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Submits a permission request. If the capability is already granted in state,
 * returns the existing grant immediately. Otherwise queues it as pending.
 * @param state - Current permissions state.
 * @param request - Permission request details.
 * @returns EditorResult wrapping the existing PermissionGrant if already authorized,
 *   or a failure with code 'PERMISSION_PENDING' if queued.
 */
export function requestPermission(
  state: PermissionsState,
  request: PermissionRequest
): EditorResult<PermissionGrant> {
  // Check if already granted
  if (checkPermission(state, request.capability as Capability, request.scope)) {
    const existing = state.grants.find(
      (g) =>
        g.capability === request.capability &&
        (g.scope === request.scope || g.scope === '*') &&
        !isGrantExpired(g)
    );
    if (existing) {
      return ok(existing, 'PERMISSION_ALREADY_GRANTED', `Capability "${request.capability}" already granted`);
    }
  }

  // Queue as pending
  const pending: PendingRequest = {
    id: generateId('req-'),
    request,
    requestedAt: now(),
  };

  // Return failure with PERMISSION_PENDING — caller knows to show a prompt
  return err(
    'PERMISSION_PENDING',
    `Permission request for "${request.capability}" in scope "${request.scope}" queued for approval`
  );
}

/**
 * Grants a permission for a capability in a scope.
 * @param state - Current permissions state.
 * @param request - The permission being granted.
 * @param scope - Scope to grant within.
 * @returns EditorResult wrapping updated PermissionsState.
 */
export function grantPermission(
  state: PermissionsState,
  request: PermissionRequest,
  scope: string
): EditorResult<PermissionsState> {
  const grant: PermissionGrant = {
    id: asPermissionId(generateId('perm-')),
    scope,
    capability: request.capability,
    trustLevel: state.trustLevel,
    grantedAt: now(),
    expiresAt: undefined,
  };

  // Remove from pending if present
  const updatedPending = state.pendingRequests.filter(
    (p) => !(p.request.capability === request.capability && p.request.scope === scope)
  );

  return ok(
    {
      ...state,
      grants: [...state.grants, grant],
      pendingRequests: updatedPending,
    },
    'PERMISSION_GRANTED',
    `Capability "${request.capability}" granted in scope "${scope}"`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §6 REVOCATION — revokePermission
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Revokes a permission grant by its ID.
 * @param state - Current permissions state.
 * @param permissionId - ID of the grant to revoke.
 * @returns EditorResult wrapping updated PermissionsState.
 */
export function revokePermission(
  state: PermissionsState,
  permissionId: PermissionId
): EditorResult<PermissionsState> {
  const grant = state.grants.find((g) => g.id === permissionId);
  if (!grant) {
    return err('PERMISSION_NOT_FOUND', `Permission "${permissionId}" not found`);
  }

  const revokedIds = new Set(state.revokedIds);
  revokedIds.add(permissionId);

  return ok(
    { ...state, revokedIds },
    'PERMISSION_REVOKED',
    `Permission "${permissionId}" (${grant.capability}) revoked`
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §7 CHECKING — checkPermission, getGrantsForScope
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Checks whether a capability is currently granted for a given scope.
 * Checks both specific scope and wildcard scope grants.
 * @param state - Current permissions state.
 * @param capability - Capability to check.
 * @param scope - Optional scope to restrict check to. Defaults to '*'.
 * @returns True if the capability is granted and not expired/revoked.
 */
export function checkPermission(
  state: PermissionsState,
  capability: Capability | string,
  scope?: string
): boolean {
  for (const grant of state.grants) {
    if (state.revokedIds.has(grant.id)) continue;
    if (isGrantExpired(grant)) continue;
    if (grant.capability !== capability) continue;
    if (scope && grant.scope !== scope && grant.scope !== '*') continue;
    return true;
  }
  return false;
}

/**
 * Returns all active (non-expired, non-revoked) grants within a scope.
 * @param state - Current permissions state.
 * @param scope - Scope to filter by.
 * @returns Array of active PermissionGrant in the scope.
 */
export function getGrantsForScope(
  state: PermissionsState,
  scope: string
): PermissionGrant[] {
  return state.grants.filter(
    (g) =>
      !state.revokedIds.has(g.id) &&
      !isGrantExpired(g) &&
      (g.scope === scope || g.scope === '*')
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// §8 TRUST LEVEL — setTrustLevel, getTrustLevel
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Sets the trust level, adding default capabilities for the new level and
 * revoking capabilities not present at the new level.
 * @param state - Current permissions state.
 * @param level - New trust level.
 * @returns EditorResult wrapping updated PermissionsState.
 */
export function setTrustLevel(
  state: PermissionsState,
  level: TrustLevel
): EditorResult<PermissionsState> {
  if (state.trustLevel === level) {
    return ok(state, 'TRUST_LEVEL_UNCHANGED', `Trust level is already "${level}"`);
  }

  const newDefaults = TRUST_LEVEL_DEFAULTS[level];
  const newGrants: PermissionGrant[] = newDefaults.map((cap) => ({
    id: asPermissionId(generateId('perm-')),
    scope: '*',
    capability: cap,
    trustLevel: level,
    grantedAt: now(),
  }));

  // Revoke all existing default grants (keep explicit/scoped grants)
  const existingRevoked = new Set(state.revokedIds);
  for (const grant of state.grants) {
    if (grant.trustLevel !== state.trustLevel) continue; // Keep explicit grants
    existingRevoked.add(grant.id);
  }

  return ok(
    {
      ...state,
      trustLevel: level,
      grants: [...state.grants.filter((g) => g.trustLevel !== state.trustLevel), ...newGrants],
      revokedIds: existingRevoked,
    },
    'TRUST_LEVEL_SET',
    `Trust level changed from "${state.trustLevel}" to "${level}"`
  );
}

/**
 * Returns the current trust level.
 * @param state - Current permissions state.
 * @returns Current TrustLevel.
 */
export function getTrustLevel(state: PermissionsState): TrustLevel {
  return state.trustLevel;
}

// ═══════════════════════════════════════════════════════════════════════════════
// §9 QUERIES — getPendingRequests, getPermissionsSummary
// ═══════════════════════════════════════════════════════════════════════════════

/**
 * Returns all unresolved permission requests.
 * @param state - Current permissions state.
 * @returns Array of PendingRequest.
 */
export function getPendingRequests(state: PermissionsState): PendingRequest[] {
  return state.pendingRequests;
}

/**
 * Returns a summary of all permission grants grouped by trust level and capability.
 * @param state - Current permissions state.
 * @returns PermissionsSummary.
 */
export function getPermissionsSummary(state: PermissionsState): PermissionsSummary {
  let activeGrants = 0;
  let expiredGrants = 0;
  const byCapability = new Map<string, number>();

  for (const grant of state.grants) {
    if (state.revokedIds.has(grant.id)) continue;
    if (isGrantExpired(grant)) {
      expiredGrants++;
    } else {
      activeGrants++;
      byCapability.set(grant.capability, (byCapability.get(grant.capability) ?? 0) + 1);
    }
  }

  return {
    trustLevel: state.trustLevel,
    totalGrants: state.grants.length,
    activeGrants,
    expiredGrants,
    pendingRequests: state.pendingRequests.length,
    byCapability,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// §10 INTERNAL HELPERS
// ═══════════════════════════════════════════════════════════════════════════════

/** Returns true if a grant has passed its expiration time. */
function isGrantExpired(grant: PermissionGrant): boolean {
  if (grant.expiresAt === undefined) return false;
  return grant.expiresAt < now();
}
